import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Check,
  LogOut,
  Volume2,
  MessageCircle,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { TRADITIONS, SEEKING_OPTIONS } from "../lib/constants";

// Per-tradition tint color (used for 500ms flash on switch)
const TRADITION_TINTS = {
  krishna: "#4a2e88",
  rumi: "#8b3a1f",
  buddha: "#b87a2e",
  stoic: "#3a3a3a",
  all: "#6b4a8a",
};

const PROVIDER_KEY_LINKS = {
  elevenlabs: "https://elevenlabs.io/app/speech-synthesis",
  openai: "https://platform.openai.com/api-keys",
  google: "https://console.cloud.google.com/apis/credentials",
};

const SETTINGS_KEY = "narrava_settings";
const defaultSettings = {
  voiceOn: true,
  rate: "normal",
  gender: "female",
  provider: "browser",
  keys: { elevenlabs: "", openai: "", google: "" },
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...defaultSettings, ...JSON.parse(raw) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
}

/**
 * Fullscreen modal for picking a tradition on mobile.
 * Renders as a bottom sheet on small screens, centered on larger.
 */
function TraditionModal({ open, current, onPick, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="fixed inset-x-0 bottom-0 z-[61] sm:inset-0 sm:flex sm:items-center sm:justify-center"
          >
            <div
              className="mx-auto w-full max-w-md rounded-t-[22px] sm:rounded-[22px] border border-[#d4af7a]/25 p-5 pb-8 sm:pb-5 shadow-2xl"
              style={{ background: "rgba(14, 8, 20, 0.98)", backdropFilter: "blur(18px)" }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-ui font-semibold text-[#f8ecd4] text-[17px]">
                  Choose your storyteller
                </h3>
                <button
                  onClick={onClose}
                  data-testid="tradition-modal-close"
                  className="p-1.5 rounded-full hover:bg-white/5 text-[#ac9470]"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-2.5 max-h-[70vh] overflow-y-auto">
                {TRADITIONS.map((t) => {
                  const active = current === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`modal-tradition-${t.id}`}
                      onClick={() => onPick(t.id)}
                      className={`w-full text-left rounded-2xl p-3 flex items-center gap-3 border transition-all ${
                        active
                          ? "border-[#eac88a]/70 bg-[#eac88a]/5"
                          : "border-[#d4af7a]/15 hover:border-[#d4af7a]/40"
                      }`}
                    >
                      <div
                        className="w-11 h-11 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/25 flex-shrink-0"
                        style={{ backgroundImage: `url(${t.image})` }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-ui font-medium text-[#f8ecd4] text-[15px] leading-tight">
                          {t.name}
                        </p>
                        <p className="text-[11px] text-[#ac9470] font-ui mt-0.5">
                          {t.subtitle}
                        </p>
                      </div>
                      {active && (
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#eac88a] to-[#a07544] flex items-center justify-center flex-shrink-0">
                          <Check size={12} strokeWidth={3} className="text-[#1a0d1d]" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function ProfilePanel({ open, onClose }) {
  const { user, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [name, setName] = useState(user?.name || "");
  const [tradition, setTradition] = useState(user?.tradition || "all");
  const [seeking, setSeeking] = useState(user?.seeking || []);
  const [settings, setSettings] = useState(loadSettings());
  const [saving, setSaving] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [tintColor, setTintColor] = useState(null);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [traditionModal, setTraditionModal] = useState(false);
  const [conversationCount, setConversationCount] = useState(0);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setTradition(user.tradition || "all");
      setSeeking(user.seeking || []);
    }
  }, [user, open]);

  useEffect(() => {
    if (open) {
      api
        .get("/conversations")
        .then((r) => setConversationCount((r.data || []).length))
        .catch(() => {});
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name: name.trim() || user.name,
        tradition,
        seeking,
      });
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    nav("/", { replace: true });
  };

  const toggleSeek = (id) =>
    setSeeking((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const pickTradition = (id) => {
    if (id !== tradition) {
      setTintColor(TRADITION_TINTS[id]);
      setTimeout(() => setTintColor(null), 550);
    }
    setTradition(id);
    setTraditionModal(false);
  };

  const currentTrad = TRADITIONS.find((t) => t.id === tradition) || TRADITIONS[4];

  if (!user) return null;

  const joinedDate = new Date(user.joined_at);
  const joinedShort = joinedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const joinedLong = joinedDate.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          {tintColor && (
            <div
              className="tint-flash"
              style={{ background: tintColor }}
              aria-hidden
            />
          )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 220 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-[92%] max-w-md border-l border-[#d4af7a]/20 flex flex-col"
            style={{ background: "rgba(12, 7, 18, 0.96)", backdropFilter: "blur(16px)" }}
          >
            {/* Header — user card */}
            <div className="flex items-start justify-between p-5 border-b border-[#d4af7a]/15">
              <div className="flex items-center gap-3.5 min-w-0">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-ui font-semibold text-[#1a0d1d] text-[20px] flex-shrink-0"
                  style={{ background: "linear-gradient(135deg,#eac88a,#a07544)" }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-ui font-semibold text-[#f8ecd4] text-[18px] leading-tight truncate">
                    {user.name}
                  </p>
                  <p className="text-[13px] text-[#ac9470] font-ui truncate mt-0.5">
                    {user.email}
                  </p>
                  <p className="text-[11px] text-[#ac9470]/70 font-ui mt-0.5">
                    Joined {joinedLong}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="profile-close"
                className="p-2 rounded-full hover:bg-white/5 text-[#ac9470] flex-shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 mx-5 mt-5 mb-2 rounded-full bg-black/30 border border-[#d4af7a]/15">
              {["profile", "settings"].map((t) => (
                <button
                  key={t}
                  data-testid={`tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`flex-1 py-2 rounded-full text-[13px] font-ui font-medium transition-all capitalize ${
                    tab === t
                      ? "bg-gradient-to-r from-[#eac88a] to-[#a07544] text-[#1a0d1d]"
                      : "text-[#ac9470]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {tab === "profile" && (
                <div className="space-y-6 pt-4">
                  {/* Stats row — Lucide icons, reduced height, all Inter */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#eac88a]/20 to-[#a07544]/10 border border-[#d4af7a]/25 flex items-center justify-center flex-shrink-0">
                        <MessageCircle size={14} strokeWidth={1.75} className="text-[#eac88a]" />
                      </div>
                      <div className="min-w-0">
                        <p
                          className="font-ui font-semibold text-[#f8ecd4] text-[18px] leading-none"
                          data-testid="stat-conversations"
                        >
                          {conversationCount}
                        </p>
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#ac9470] font-ui mt-1">
                          Talks
                        </p>
                      </div>
                    </div>
                    <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#eac88a]/20 to-[#a07544]/10 border border-[#d4af7a]/25 flex items-center justify-center flex-shrink-0">
                        <Calendar size={14} strokeWidth={1.75} className="text-[#eac88a]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-ui font-semibold text-[#f8ecd4] text-[18px] leading-none">
                          {joinedShort}
                        </p>
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#ac9470] font-ui mt-1">
                          Joined
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Display name */}
                  <div className="space-y-2">
                    <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                      Display name
                    </label>
                    <input
                      data-testid="settings-name-input"
                      className="nv-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  {/* Your storyteller — compact, with Change modal trigger */}
                  <div className="space-y-2">
                    <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                      Your storyteller
                    </label>
                    <button
                      onClick={() => setTraditionModal(true)}
                      data-testid="change-tradition-btn"
                      className="w-full glass rounded-2xl p-3 flex items-center gap-3 hover:border-[#d4af7a]/45 transition-all"
                    >
                      <div
                        className="w-11 h-11 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/30 flex-shrink-0"
                        style={{ backgroundImage: `url(${currentTrad.image})` }}
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <p className="font-ui font-medium text-[#f8ecd4] text-[15px] leading-tight">
                          {currentTrad.name}
                        </p>
                        <p className="text-[11px] text-[#ac9470] font-ui mt-0.5">
                          {currentTrad.subtitle}
                        </p>
                      </div>
                      <span className="text-[12px] font-ui text-[#f0d09c] flex items-center gap-0.5 flex-shrink-0">
                        Change <ChevronRight size={13} strokeWidth={1.75} />
                      </span>
                    </button>
                  </div>

                  {/* Seeking — half the previous top margin handled by space-y-6 above */}
                  <div className="space-y-2">
                    <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                      You are seeking
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {SEEKING_OPTIONS.map((s) => {
                        const active = seeking.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            data-testid={`settings-seeking-${s.id}`}
                            onClick={() => toggleSeek(s.id)}
                            className={`rounded-full px-3 py-1.5 text-[12px] font-ui border transition-all ${
                              active
                                ? "bg-gradient-to-r from-[#eac88a]/20 to-[#a07544]/20 border-[#eac88a]/70 text-[#f8ecd4]"
                                : "border-[#d4af7a]/20 text-[#ac9470] hover:border-[#d4af7a]/50"
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {tab === "settings" && (
                <div className="space-y-6 pt-4">
                  {/* Voice master toggle */}
                  <div className="glass rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#eac88a]/20 to-[#a07544]/10 border border-[#d4af7a]/25 flex items-center justify-center">
                        <Volume2 size={15} className="text-[#eac88a]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-ui font-medium text-[#f8ecd4] text-[14px]">
                          Voice responses
                        </p>
                        <p className="text-[11px] text-[#ac9470] font-ui mt-0.5">
                          Narrator reads aloud
                        </p>
                      </div>
                    </div>
                    <button
                      data-testid="toggle-voice"
                      onClick={() =>
                        setSettings((s) => ({ ...s, voiceOn: !s.voiceOn }))
                      }
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        settings.voiceOn
                          ? "bg-gradient-to-r from-[#eac88a] to-[#a07544]"
                          : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          settings.voiceOn ? "left-5" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Speed */}
                  <div className="space-y-2">
                    <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                      Voice speed
                    </label>
                    <div className="flex gap-2">
                      {["slow", "normal", "fast"].map((r) => (
                        <button
                          key={r}
                          data-testid={`rate-${r}`}
                          onClick={() => setSettings((s) => ({ ...s, rate: r }))}
                          className={`flex-1 rounded-full py-2 text-[13px] capitalize font-ui border transition-all ${
                            settings.rate === r
                              ? "bg-gradient-to-r from-[#eac88a]/20 to-[#a07544]/20 border-[#eac88a]/70 text-[#f8ecd4]"
                              : "border-[#d4af7a]/20 text-[#ac9470]"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="space-y-2">
                    <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                      Voice
                    </label>
                    <div className="flex gap-2">
                      {[
                        { id: "female", label: "Female" },
                        { id: "male", label: "Male" },
                      ].map((g) => (
                        <button
                          key={g.id}
                          data-testid={`gender-${g.id}`}
                          onClick={() => setSettings((s) => ({ ...s, gender: g.id }))}
                          className={`flex-1 rounded-full py-2 text-[13px] font-ui border transition-all ${
                            settings.gender === g.id
                              ? "bg-gradient-to-r from-[#eac88a]/20 to-[#a07544]/20 border-[#eac88a]/70 text-[#f8ecd4]"
                              : "border-[#d4af7a]/20 text-[#ac9470]"
                          }`}
                        >
                          {g.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#ac9470]/70 font-ui pl-0.5">Browser default</p>
                  </div>

                  {/* Premium voices — collapsed disclosure */}
                  <div className="border-t border-[#d4af7a]/12 pt-5">
                    <button
                      data-testid="toggle-premium-voices"
                      onClick={() => setPremiumOpen((v) => !v)}
                      className="w-full flex items-center justify-between py-1 group"
                    >
                      <span className="text-[11px] tracking-[0.2em] uppercase text-[#ac9470] font-ui font-medium group-hover:text-[#f8ecd4] transition-colors">
                        Premium voices (advanced)
                      </span>
                      <ChevronDown
                        size={15}
                        className={`text-[#ac9470] transition-transform ${
                          premiumOpen ? "rotate-180" : ""
                        }`}
                        strokeWidth={1.75}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {premiumOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 space-y-3">
                            <p className="text-[12.5px] text-[#f8ecd4]/80 font-ui leading-[1.5]">
                              Use premium TTS for a more natural voice. Bring your own key —
                              it stays on this device.
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { id: "browser", label: "Browser" },
                                { id: "elevenlabs", label: "ElevenLabs" },
                                { id: "openai", label: "OpenAI" },
                                { id: "google", label: "Google TTS" },
                              ].map((p) => (
                                <button
                                  key={p.id}
                                  data-testid={`provider-${p.id}`}
                                  onClick={() =>
                                    setSettings((s) => ({ ...s, provider: p.id }))
                                  }
                                  className={`rounded-xl py-2 text-[12.5px] font-ui border transition-all ${
                                    settings.provider === p.id
                                      ? "bg-gradient-to-r from-[#eac88a]/20 to-[#a07544]/20 border-[#eac88a]/70 text-[#f8ecd4]"
                                      : "border-[#d4af7a]/20 text-[#ac9470]"
                                  }`}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                            {settings.provider !== "browser" && (
                              <div className="space-y-2 pt-1">
                                <label className="text-[11px] text-[#ac9470] font-ui font-medium">
                                  API key
                                </label>
                                <input
                                  data-testid={`key-${settings.provider}`}
                                  type="password"
                                  className="nv-input text-[13px]"
                                  placeholder="Paste your key…"
                                  value={settings.keys?.[settings.provider] || ""}
                                  onChange={(e) =>
                                    setSettings((s) => ({
                                      ...s,
                                      keys: { ...s.keys, [settings.provider]: e.target.value },
                                    }))
                                  }
                                />
                                <a
                                  href={PROVIDER_KEY_LINKS[settings.provider]}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[12px] text-[#f0d09c] hover:text-[#f8ecd4] font-ui"
                                >
                                  How to get a key <ExternalLink size={11} strokeWidth={1.75} />
                                </a>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom actions (unchanged layout) */}
            <div className="p-5 border-t border-[#d4af7a]/15 space-y-2">
              <button
                data-testid="settings-save"
                onClick={save}
                disabled={saving}
                className="btn-gold w-full rounded-full py-3 font-ui font-medium disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {confirmLogout ? (
                <div className="glass rounded-2xl p-3 space-y-2">
                  <p className="text-[13px] text-[#f8ecd4]/90 font-ui text-center">
                    Leave Narrava for now?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmLogout(false)}
                      className="flex-1 rounded-full py-2 text-[12px] font-ui border border-[#d4af7a]/25 text-[#ac9470]"
                    >
                      Stay
                    </button>
                    <button
                      data-testid="confirm-logout"
                      onClick={doLogout}
                      className="flex-1 rounded-full py-2 text-[12px] font-ui bg-red-900/40 border border-red-500/40 text-red-200"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="logout-btn"
                  onClick={() => setConfirmLogout(true)}
                  className="w-full rounded-full py-3 font-ui font-medium text-red-300 border border-red-500/30 hover:bg-red-500/10 flex items-center justify-center gap-2"
                >
                  <LogOut size={15} strokeWidth={1.75} /> Sign out
                </button>
              )}
            </div>
          </motion.aside>

          <TraditionModal
            open={traditionModal}
            current={tradition}
            onPick={pickTradition}
            onClose={() => setTraditionModal(false)}
          />
        </>
      )}
    </AnimatePresence>
  );
}
